import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { FilteredState, PaginationParams, SortParams, FilterParams } from '../types';
import { appConfig } from '@/config/app-config';
export interface Port {
  id: number;
  code: string;
  name: string;
  country_id: number | null;
  city: string | null;
  is_active: boolean;
  created_at: string;
  created_by: string | null;
  updated_at: string | null;
  updated_by: string | null;
}
// Interfaz para opciones de filtro (usado en dropdowns)
export interface PortFilterOption {
  value: string;
  label: string;
  city?: string;
  country_name?: string;
}
interface PortsState extends FilteredState<Port> {
  selectedPort: Port | null;
  creating: boolean;
  updating: boolean;
  deleting: boolean;
  // Estado para opciones de filtro (cacheadas para evitar recargas)
  filterOptions: PortFilterOption[];
  filterOptionsLoading: boolean;
  filterOptionsError: string | null;
  filterOptionsLastFetch: number | null;
}
const initialState: PortsState = {
  items: [],
  totalCount: 0,
  currentPage: 1,
  pageSize: 50,
  loading: false,
  error: null,
  lastFetch: null,
  filters: {},
  sortField: 'code',
  sortDirection: 'asc',
  searchTerm: null,
  selectedPort: null,
  creating: false,
  updating: false,
  deleting: false,
  // Estado para opciones de filtro
  filterOptions: [],
  filterOptionsLoading: false,
  filterOptionsError: null,
  filterOptionsLastFetch: null,
};
// Thunk para cargar ports con paginación y filtros (para vistas de lista)
export const fetchPorts = createAsyncThunk<{ data: Port[]; totalCount: number }, any>(
  'ports/fetchPorts',
  async (params: any) => {
    throw new Error('Not implemented yet');
  }
);
// Thunk para cargar opciones de filtro (solo id, nombre, ciudad) - CACHEADO
export const fetchPortFilterOptions = createAsyncThunk(
  'ports/fetchFilterOptions',
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as any;
    const portsState = state.ports as PortsState;
    // ✅ CACHE: Si ya tenemos datos frescos (menos de 5 minutos), usar cache
    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos
    if (
      portsState.filterOptionsLastFetch &&
      Date.now() - portsState.filterOptionsLastFetch < CACHE_DURATION &&
      portsState.filterOptions.length > 0
    ) {
      return portsState.filterOptions;
    }
    try {
      // Obtener token de autenticación
      const token = localStorage.getItem('authToken') || '';
      const url = appConfig.getApiUrl('masters/terminals?startRow=0&endRow=500&sortProperty=name&sortDirection=asc');
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(`Error ${response.status}: ${errorText}`);
      }
      const result = await response.json();
      const ports = result?.data?.rows || [];
      // Transformar a formato de opciones de filtro
      const filterOptions: PortFilterOption[] = ports.map((port: any) => ({
        value: port.id.toString(),
        label: port.name,
        city: port.city,
        country_name: port.country?.name
      }));
      return filterOptions;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Error al cargar opciones de filtro');
    }
  }
);
export const fetchPortById = createAsyncThunk('ports/fetchPortById', async (id: number) => {
  throw new Error('Not implemented yet');
});
export const createPort = createAsyncThunk('ports/createPort', async (port: Partial<Port>) => {
  throw new Error('Not implemented yet');
});
export const updatePort = createAsyncThunk('ports/updatePort', async ({ id, data }: { id: number; data: Partial<Port> }) => {
  throw new Error('Not implemented yet');
});
export const deletePort = createAsyncThunk('ports/deletePort', async (id: number) => {
  throw new Error('Not implemented yet');
});
const portsSlice = createSlice({
  name: 'ports',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<FilterParams>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.currentPage = 1;
    },
    clearFilters: (state) => {
      state.filters = {};
      state.searchTerm = null;
      state.currentPage = 1;
    },
    setSearchTerm: (state, action: PayloadAction<string | null>) => {
      state.searchTerm = action.payload;
      state.currentPage = 1;
    },
    setSort: (state, action: PayloadAction<SortParams>) => {
      state.sortField = action.payload.field;
      state.sortDirection = action.payload.direction;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      state.currentPage = 1;
    },
    selectPort: (state, action: PayloadAction<Port | null>) => {
      state.selectedPort = action.payload;
    },
    clearSelection: (state) => {
      state.selectedPort = null;
    },
  },
  extraReducers: (builder) => {
    // Casos para fetchPorts (lista completa)
    builder
      .addCase(fetchPorts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPorts.fulfilled, (state, action: PayloadAction<{ data: Port[]; totalCount: number }>) => {
        state.loading = false;
        state.items = action.payload.data;
        state.totalCount = action.payload.totalCount;
        state.lastFetch = Date.now();
      })
      .addCase(fetchPorts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Error al cargar puertos';
      })
      // Casos para fetchPortFilterOptions (opciones cacheadas)
      .addCase(fetchPortFilterOptions.pending, (state) => {
        state.filterOptionsLoading = true;
        state.filterOptionsError = null;
      })
      .addCase(fetchPortFilterOptions.fulfilled, (state, action) => {
        state.filterOptionsLoading = false;
        state.filterOptions = action.payload;
        state.filterOptionsLastFetch = Date.now();
      })
      .addCase(fetchPortFilterOptions.rejected, (state, action) => {
        state.filterOptionsLoading = false;
        state.filterOptionsError = action.payload as string || 'Error al cargar opciones de filtro';
      });
  },
});
// Selectores para acceder al estado
export const selectPortFilterOptions = (state: any) => state.ports.filterOptions;
export const selectPortFilterOptionsLoading = (state: any) => state.ports.filterOptionsLoading;
export const selectPortFilterOptionsError = (state: any) => state.ports.filterOptionsError;
// Helper para verificar si las opciones de filtro están frescas
export const arePortFilterOptionsFresh = (state: any): boolean => {
  const lastFetch = state.ports.filterOptionsLastFetch;
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos
  return lastFetch && Date.now() - lastFetch < CACHE_DURATION;
};
export const { setFilters, clearFilters, setSearchTerm, setSort, setPage, setPageSize, selectPort, clearSelection } = portsSlice.actions;
export default portsSlice.reducer;
